import React, { useState } from "react";
import { gql, useQuery } from '@apollo/client';
import { makeStyles } from "@material-ui/core/styles";
import ListSubheader from "@material-ui/core/ListSubheader";
import List from "@material-ui/core/List";
import Typography from "@material-ui/core/Typography";
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import ProductListContext from "./ProductListContext";
import ProductListItem from "./ProductListItem";
import ProductFilters from "./ProductFilters";

const useStyles = makeStyles((theme) => ({
    root: {
        position: "relative",
        overflow: "auto",
        width: '100%',
    },
    list: {
        position: "relative",
        overflow: "auto",
        width: '100%',
    },
    listSection: {
        backgroundColor: theme.palette.background.paper
    },
    listSubheader: {
        backgroundColor: theme.palette.grey[100],
        color: "black",
        paddingTop: "8px",
        paddingBottom: "8px",
        borderBottom: "1px solid " + theme.palette.grey[200]
    },
    ul: {
        backgroundColor: "inherit",
        padding: 0
    },
    "@media (min-width: 1080px)": {
        list: {
            boxShadow: theme.shadows[5],
        },
    },
    loadMoreContainer: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(2),
    },
    loadMoreButton: {
        minWidth: 120,
    }
}));

const PRODUCTS_QUERY = gql`
    query AllProducts($page: Int, $keyword: String, $filter: ProductFilter) {
        allProducts(page: $page, keyword: $keyword, filter: $filter) {
            id
            code
            description
            category
            type
            style
            species
            brand
            supplier
            uom
        }
    }
`;

const ProductList = () => {
    const classes = useStyles();
    const [keyword, setKeyword] = useState(() => {
        return localStorage.getItem('productSearchKeyword') || '';
    });
    const [page, setPage] = useState(0);
    const [allProducts, setAllProducts] = useState([]);

    const { data, loading, error, fetchMore, refetch } = useQuery(PRODUCTS_QUERY, {
        variables: {
            page: 0,
            keyword: keyword || undefined,
        },
        onCompleted: (data) => {
            if (page === 0) {
                setAllProducts(data.allProducts || []);
            }
        }
    });

    React.useEffect(() => {
        if (keyword !== undefined) {
            refetch({
                page: 0,
                keyword: keyword || undefined,
            });
        }
    }, [keyword, refetch]);

    const handleKeywordChange = (newKeyword) => {
        setKeyword(newKeyword);
        setPage(0);
        setAllProducts([]);
        
        if (newKeyword) {
            localStorage.setItem('productSearchKeyword', newKeyword);
        } else {
            localStorage.removeItem('productSearchKeyword');
        }
    };

    const handleLoadMore = async () => {
        const nextPage = page + 1;
        try {
            const { data: moreData } = await fetchMore({
                variables: {
                    page: nextPage,
                    keyword: keyword || undefined,
                }
            });
            
            if (moreData.allProducts && moreData.allProducts.length > 0) {
                setAllProducts(prev => [...prev, ...moreData.allProducts]);
                setPage(nextPage);
            }
        } catch (err) {
            console.error('Error loading more products:', err);
        }
    };

    if (loading && page === 0) {
        return <CircularProgress />
    }

    if (error) {
        return <div>Error loading products</div>
    }

    const allProductsArr = allProducts;

    const productsByCategory = allProductsArr.reduce((acc, product) => {
        const category = product.category || 'Other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(product);
        return acc;
    }, {});

    const hasMoreData = data && data.allProducts && data.allProducts.length === 20;

    return (
        <ProductListContext.Consumer>
            {
                (context) => {
                    return (
                        <div className={classes.root}>
                            <ProductFilters 
                                keyword={keyword}
                                onKeywordChange={handleKeywordChange}
                            />
                            <List className={classes.list}>
                                {
                                    Object.keys(productsByCategory).map((category, categoryIndex) => (
                                        <li key={`section-${categoryIndex}`} className={classes.listSection}>
                                            <ul className={classes.ul}>
                                                <ListSubheader className={classes.listSubheader}>
                                                    <Typography variant="subtitle1">{category} ({productsByCategory[category].length})</Typography>
                                                </ListSubheader>
                                                {
                                                    productsByCategory[category].map((product, i) => (
                                                        <ProductListItem 
                                                            context={context} 
                                                            product={product} 
                                                            key={product.id} 
                                                            data-id={product.id} 
                                                            count={i + 1}
                                                        />
                                                    ))
                                                }
                                            </ul>
                                        </li>
                                    ))
                                }
                            </List>
                            {hasMoreData && (
                                <Box className={classes.loadMoreContainer}>
                                    <Button
                                        className={classes.loadMoreButton}
                                        variant="outlined"
                                        color="primary"
                                        onClick={handleLoadMore}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={20} /> : 'Load More'}
                                    </Button>
                                </Box>
                            )}
                        </div>
                    )
                }
            }
        </ProductListContext.Consumer>
    );
}

export default ProductList
